import { createNoise3D, type NoiseFunction3D } from 'simplex-noise';
import { resolvePalette } from '../../core/palette';
import { DEG, clamp01 } from '../../core/math';
import { bakeCurve, sampleBaked, type Curve } from '../../core/params/curve';
import { createCanvas, get2d } from '../../core/text/rasterize';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { FlowFieldValues } from './params';

/**
 * Flow field.
 *
 * Particles follow a Perlin/simplex vector field and leave streamlines behind; the
 * text's distance field bends them on the way past. Two implementation choices carry
 * the whole technique:
 *
 *  - **Struct-of-arrays state.** At 200 000 particles an array of objects costs an
 *    allocation each and destroys locality in the only loop that matters.
 *  - **An accumulation buffer.** Streamlines are drawn once into an offscreen canvas
 *    and blitted every frame. Redrawing hundreds of thousands of segments per frame
 *    would be impossible; this way a finished field costs a single `drawImage`.
 */
/**
 * Segments are not stroked individually. Each one is filed into a bucket by quantised
 * colour and width, and a bucket is stroked as a single path.
 *
 * This is the difference between a usable technique and an unusable one: setting
 * `strokeStyle` re-parses a CSS colour string, and every `stroke()` revalidates the
 * context state. Measured at 4 200 particles, per-segment stroking ran at ~357 000
 * segments per second; bucketing lifts that several times over, with no visible
 * difference — 48 colour steps and 8 width steps are far below what a 0.34-alpha
 * stroke can show.
 */
const COLOR_BUCKETS = 48;
const WIDTH_BUCKETS = 8;

interface SegmentBucket {
  coords: Float32Array;
  n: number;
}

export interface FlowFieldState {
  buffer: OffscreenCanvas | HTMLCanvasElement;
  bctx: Ctx2D;

  buckets: SegmentBucket[];
  bucketStyles: string[];
  bucketWidths: Float32Array;

  x: Float32Array;
  y: Float32Array;
  age: Uint16Array;
  /** Per-particle random value, used by the random colour mode. */
  tint: Float32Array;
  count: number;

  /**
   * Precomputed spawn candidates as [x, y, nx, ny] quadruples. Sampling the outline
   * walks every contour, so it happens once here rather than per respawn.
   */
  spawn: Float32Array | null;

  noise: NoiseFunction3D;
  taper: Float32Array;

  /** Cached vector field, see `ensureGrid`. Null when sampling directly is cheaper. */
  grid: VectorGrid | null;

  /** Simulation time, feeding the third noise dimension. */
  time: number;
  /** Completed lifetimes, as a fraction of the particle population. */
  pass: number;
  done: boolean;
  segments: number;
}

/**
 * Cached vector field.
 *
 * Curl noise costs four fBm evaluations — eight simplex calls at two octaves — for
 * every particle on every step, and that dominates the simulation. Sampling the field
 * onto a grid once and interpolating turns it into two array reads.
 *
 * It is only a win when there are more particle evaluations than grid nodes, so the
 * decision is made per configuration: always cache a static field, cache an evolving
 * one only above the break-even particle count, and otherwise evaluate directly.
 *
 * Directions are stored as cos/sin rather than angles. Interpolating angles across the
 * ±π wrap would produce a hard seam sweeping through the artwork.
 */
interface VectorGrid {
  cell: number;
  w: number;
  h: number;
  cos: Float32Array;
  sin: Float32Array;
  /** Noise time this grid was built for; a change forces a rebuild. */
  time: number;
}

/** Fractal Brownian motion over 3D simplex noise. */
function fbm(noise: NoiseFunction3D, x: number, y: number, z: number, octaves: number, persistence: number): number {
  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;
  for (let i = 0; i < octaves; i++) {
    sum += noise(x * freq, y * freq, z) * amp;
    norm += amp;
    amp *= persistence;
    freq *= 2;
  }
  return norm > 0 ? sum / norm : 0;
}

function values(ctx: TechniqueContext): FlowFieldValues {
  return ctx.values as unknown as FlowFieldValues;
}

function buildSpawnTable(ctx: TechniqueContext, v: FlowFieldValues, count: number): Float32Array | null {
  const samples = Math.max(64, Math.min(6000, count));
  if (v.origin === 'outline') {
    const outline = ctx.field.sampleOutline(samples);
    if (!outline.length) return null;
    const table = new Float32Array(outline.length * 4);
    outline.forEach((s, i) => {
      table[i * 4] = s.x;
      table[i * 4 + 1] = s.y;
      table[i * 4 + 2] = s.nx;
      table[i * 4 + 3] = s.ny;
    });
    return table;
  }
  if (v.origin === 'inside') {
    const pts = ctx.field.samplePoints(samples, 'poisson', ctx.rng);
    if (pts.length < 2) return null;
    const table = new Float32Array((pts.length / 2) * 4);
    for (let i = 0; i < pts.length / 2; i++) {
      table[i * 4] = pts[i * 2];
      table[i * 4 + 1] = pts[i * 2 + 1];
    }
    return table;
  }
  return null;
}

/** Places one particle according to the chosen emission source. */
function seedParticle(state: FlowFieldState, i: number, ctx: TechniqueContext, v: FlowFieldValues): void {
  const { width, height, rng } = ctx;
  let x = 0;
  let y = 0;

  switch (v.origin) {
    case 'grid': {
      const cols = Math.max(1, Math.round(Math.sqrt((state.count * width) / height)));
      const rows = Math.max(1, Math.ceil(state.count / cols));
      x = ((i % cols) + 0.5 + rng.signed(0.35)) * (width / cols);
      y = ((Math.floor(i / cols) % rows) + 0.5 + rng.signed(0.35)) * (height / rows);
      break;
    }
    case 'edges': {
      const side = rng.int(0, 3);
      const t = rng.next();
      if (side === 0) {
        x = t * width;
        y = 0;
      } else if (side === 1) {
        x = width;
        y = t * height;
      } else if (side === 2) {
        x = t * width;
        y = height;
      } else {
        x = 0;
        y = t * height;
      }
      break;
    }
    case 'outline':
    case 'inside': {
      const table = state.spawn;
      if (table && table.length >= 4) {
        const k = rng.int(0, table.length / 4 - 1) * 4;
        // Push outline spawns just clear of the contour so lines hug the letterforms
        // instead of starting exactly on the edge, where they would be killed at once.
        const offset = v.origin === 'outline' ? 1 + rng.next() * 6 : 0;
        x = table[k] + table[k + 2] * offset;
        y = table[k + 1] + table[k + 3] * offset;
      } else {
        x = rng.next() * width;
        y = rng.next() * height;
      }
      break;
    }
    case 'outside': {
      for (let attempt = 0; attempt < 40; attempt++) {
        x = rng.next() * width;
        y = rng.next() * height;
        if (!ctx.field.isInside(x, y)) break;
      }
      break;
    }
    default:
      x = rng.next() * width;
      y = rng.next() * height;
  }

  state.x[i] = x;
  state.y[i] = y;
}

export function init(ctx: TechniqueContext): FlowFieldState {
  const v = values(ctx);
  const count = Math.max(1, Math.round(v.particles));

  const buffer = createCanvas(ctx.width, ctx.height);
  const bctx = get2d(buffer);
  bctx.lineCap = 'round';
  bctx.lineJoin = 'round';

  const palette = resolvePalette(v.palette, ctx.rng.fork(1));
  // Solid mode uses the palette's first stop; every other mode walks the whole ramp.
  const bucketStyles = Array.from({ length: COLOR_BUCKETS }, (_, i) =>
    v.colorMode === 'solid' ? palette.stop(0, v.opacity) : palette.at(i / (COLOR_BUCKETS - 1), v.opacity),
  );
  const widthLo = v.widthMode === 'fixed' ? v.lineWidth : v.lineWidth * 0.15;
  const widthHi = v.widthMode === 'fixed' ? v.lineWidth : v.lineWidth * 1.5;
  const bucketWidths = new Float32Array(WIDTH_BUCKETS);
  for (let i = 0; i < WIDTH_BUCKETS; i++) {
    bucketWidths[i] = widthLo + ((widthHi - widthLo) * i) / (WIDTH_BUCKETS - 1);
  }

  const state: FlowFieldState = {
    buffer,
    bctx,
    buckets: Array.from({ length: COLOR_BUCKETS * WIDTH_BUCKETS }, () => ({
      coords: new Float32Array(256),
      n: 0,
    })),
    bucketStyles,
    bucketWidths,
    x: new Float32Array(count),
    y: new Float32Array(count),
    age: new Uint16Array(count),
    tint: new Float32Array(count),
    count,
    spawn: null,
    // simplex-noise takes a PRNG, so the field is reproducible from the layer seed.
    noise: createNoise3D(() => ctx.rng.next()),
    taper: bakeCurve(v.taper as Curve),
    grid: null,
    time: 0,
    pass: 0,
    done: false,
    segments: 0,
  };

  state.spawn = buildSpawnTable(ctx, v, count);

  for (let i = 0; i < count; i++) {
    seedParticle(state, i, ctx, v);
    state.tint[i] = ctx.rng.next();
    // Stagger the ages so lines do not all restart on the same step, which would show
    // up as banding in the accumulated image.
    state.age[i] = ctx.rng.int(0, Math.max(1, v.lifetime - 1));
  }

  return state;
}

export function step(state: FlowFieldState, _dt: number, ctx: TechniqueContext): FlowFieldState {
  if (state.done) return state;

  const v = values(ctx);
  const { bctx, noise } = state;
  const { width, height } = ctx;

  const scale = v.noiseScale;
  const rotation = v.fieldRotation * DEG;
  const curlEps = 1 / Math.max(1e-6, scale) / 400;
  const maxDistance = Math.max(1, v.textRadius);
  const useText = v.textMode !== 'none';
  const needDistance = useText || v.colorMode === 'distance';
  const distanceScale = Math.max(1, ctx.field.maxOutside * 0.55);

  // The preview lowers `quality` when frames get expensive; spending fewer iterations
  // per frame keeps interaction smooth without changing the final image.
  const iterations = Math.max(1, Math.round(v.iterationsPerFrame * clamp01(ctx.quality)));

  const evaluate = (x: number, y: number, t: number): number => {
    if (!v.curl) {
      return fbm(noise, x * scale, y * scale, t, v.octaves, v.persistence) * Math.PI * 2 + rotation;
    }
    // Curl noise: the perpendicular of the gradient of a scalar noise field. The result
    // is divergence-free, so streamlines braid and swirl instead of collapsing into
    // sources and sinks — much closer to how smoke or water actually moves.
    const n1 = fbm(noise, x * scale, (y + curlEps) * scale, t, v.octaves, v.persistence);
    const n2 = fbm(noise, x * scale, (y - curlEps) * scale, t, v.octaves, v.persistence);
    const n3 = fbm(noise, (x + curlEps) * scale, y * scale, t, v.octaves, v.persistence);
    const n4 = fbm(noise, (x - curlEps) * scale, y * scale, t, v.octaves, v.persistence);
    return Math.atan2(-(n3 - n4), n1 - n2) + rotation;
  };

  // Cell size tracks the feature size of the noise (roughly 1/scale px), sampled at
  // about 40 nodes per feature, so the grid never smooths away visible detail.
  const cell = Math.max(4, Math.min(48, Math.round(1 / Math.max(1e-6, scale) / 40)));
  const gridW = Math.ceil(width / cell) + 2;
  const gridH = Math.ceil(height / cell) + 2;
  const useGrid = v.timeEvolution === 0 || state.count > gridW * gridH;

  const buildGrid = (t: number): VectorGrid => {
    const grid: VectorGrid = {
      cell,
      w: gridW,
      h: gridH,
      cos: new Float32Array(gridW * gridH),
      sin: new Float32Array(gridW * gridH),
      time: t,
    };
    for (let gy = 0; gy < gridH; gy++) {
      for (let gx = 0; gx < gridW; gx++) {
        const a = evaluate(gx * cell, gy * cell, t);
        grid.cos[gy * gridW + gx] = Math.cos(a);
        grid.sin[gy * gridW + gx] = Math.sin(a);
      }
    }
    return grid;
  };

  const sampleGrid = (grid: VectorGrid, x: number, y: number): number => {
    const fx = Math.max(0, Math.min(grid.w - 1.001, x / grid.cell));
    const fy = Math.max(0, Math.min(grid.h - 1.001, y / grid.cell));
    const x0 = fx | 0;
    const y0 = fy | 0;
    const tx = fx - x0;
    const ty = fy - y0;
    const i00 = y0 * grid.w + x0;
    const i10 = i00 + 1;
    const i01 = i00 + grid.w;
    const i11 = i01 + 1;
    const c =
      (grid.cos[i00] * (1 - tx) + grid.cos[i10] * tx) * (1 - ty) +
      (grid.cos[i01] * (1 - tx) + grid.cos[i11] * tx) * ty;
    const s =
      (grid.sin[i00] * (1 - tx) + grid.sin[i10] * tx) * (1 - ty) +
      (grid.sin[i01] * (1 - tx) + grid.sin[i11] * tx) * ty;
    return Math.atan2(s, c);
  };

  for (let iter = 0; iter < iterations && !state.done; iter++) {
    if (v.trailFade > 0) {
      bctx.globalCompositeOperation = 'destination-out';
      bctx.fillStyle = `rgba(0,0,0,${v.trailFade})`;
      bctx.fillRect(0, 0, width, height);
      bctx.globalCompositeOperation = 'source-over';
    }

    state.time += v.timeEvolution;

    let grid: VectorGrid | null = null;
    if (useGrid) {
      if (!state.grid || state.grid.cell !== cell || state.grid.time !== state.time) {
        state.grid = buildGrid(state.time);
      }
      grid = state.grid;
    }

    for (let i = 0; i < state.count; i++) {
      const x = state.x[i];
      const y = state.y[i];

      let angle = grid ? sampleGrid(grid, x, y) : evaluate(x, y, state.time);
      const distance = needDistance ? ctx.field.distance(x, y) : 0;

      if (useText) {
        const falloff = 1 - clamp01(Math.abs(distance) / maxDistance);
        if (falloff > 0) {
          const [gx, gy] = ctx.field.gradient(x, y);
          const gradientAngle = Math.atan2(gy, gx);
          let target = gradientAngle;
          if (v.textMode === 'attract' || v.textMode === 'sink') target = gradientAngle + Math.PI;
          else if (v.textMode === 'tangential') target = gradientAngle + Math.PI / 2;
          // Blending the *angle* rather than adding a force keeps the step length
          // constant, so line spacing stays even around the letters.
          const delta = ((target - angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
          angle += delta * clamp01(falloff * v.textStrength);
        }
      }

      let nx = x + Math.cos(angle) * v.stepSize;
      let ny = y + Math.sin(angle) * v.stepSize;

      state.age[i] += 1;
      let respawn = state.age[i] >= v.lifetime;
      let jumped = false;

      if (v.wrap) {
        if (nx < 0) {
          nx += width;
          jumped = true;
        } else if (nx >= width) {
          nx -= width;
          jumped = true;
        }
        if (ny < 0) {
          ny += height;
          jumped = true;
        } else if (ny >= height) {
          ny -= height;
          jumped = true;
        }
      } else if (nx < -8 || ny < -8 || nx > width + 8 || ny > height + 8) {
        respawn = true;
      }

      if (v.killInside && ctx.field.isInside(nx, ny)) respawn = true;

      // A wrapped particle teleports; drawing that segment would streak right across
      // the canvas, so it is skipped on the step where it happens.
      if (!respawn && !jumped) {
        pushSegment(state, v, i, x, y, nx, ny, distance / distanceScale, angle);
        state.segments++;
      }

      if (respawn) {
        seedParticle(state, i, ctx, v);
        state.age[i] = 0;
        state.pass += 1 / state.count;
        if (state.pass >= v.passes) {
          state.done = true;
          break;
        }
      } else {
        state.x[i] = nx;
        state.y[i] = ny;
      }
    }

    flushBuckets(state);
  }

  return state;
}

/** Files a segment into its colour/width bucket, growing the bucket as needed. */
function pushSegment(
  state: FlowFieldState,
  v: FlowFieldValues,
  i: number,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  normalisedDistance: number,
  angle: number,
): void {
  const lifeT = state.age[i] / Math.max(1, v.lifetime);

  let widthT: number;
  if (v.widthMode === 'life') widthT = clamp01(sampleBaked(state.taper, lifeT));
  else if (v.widthMode === 'speed') widthT = clamp01(v.stepSize / 8);
  else widthT = 0;

  let colorT: number;
  switch (v.colorMode) {
    case 'distance':
      colorT = clamp01(Math.abs(normalisedDistance));
      break;
    case 'angle':
      colorT = (((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)) / (Math.PI * 2);
      break;
    case 'speed':
      colorT = clamp01(v.stepSize / 12);
      break;
    case 'life':
      colorT = lifeT;
      break;
    case 'random':
      colorT = state.tint[i];
      break;
    default:
      colorT = 0;
  }

  const ci = Math.min(COLOR_BUCKETS - 1, (colorT * COLOR_BUCKETS) | 0);
  const wi = Math.min(WIDTH_BUCKETS - 1, (widthT * WIDTH_BUCKETS) | 0);
  const bucket = state.buckets[wi * COLOR_BUCKETS + ci];

  if (bucket.n + 4 > bucket.coords.length) {
    const grown = new Float32Array(bucket.coords.length * 2);
    grown.set(bucket.coords);
    bucket.coords = grown;
  }
  bucket.coords[bucket.n++] = x0;
  bucket.coords[bucket.n++] = y0;
  bucket.coords[bucket.n++] = x1;
  bucket.coords[bucket.n++] = y1;
}

/** Strokes every non-empty bucket as one path and empties it. */
function flushBuckets(state: FlowFieldState): void {
  const { bctx, buckets } = state;
  for (let b = 0; b < buckets.length; b++) {
    const bucket = buckets[b];
    if (bucket.n === 0) continue;
    bctx.strokeStyle = state.bucketStyles[b % COLOR_BUCKETS];
    bctx.lineWidth = state.bucketWidths[(b / COLOR_BUCKETS) | 0];
    bctx.beginPath();
    const c = bucket.coords;
    for (let i = 0; i < bucket.n; i += 4) {
      bctx.moveTo(c[i], c[i + 1]);
      bctx.lineTo(c[i + 2], c[i + 3]);
    }
    bctx.stroke();
    bucket.n = 0;
  }
}

export function isComplete(state: FlowFieldState): boolean {
  return state.done;
}

export function draw(state: FlowFieldState, target: RenderTarget): void {
  target.ctx.drawImage(state.buffer as CanvasImageSource, 0, 0, target.width, target.height);
}

export function elementCount(state: FlowFieldState): number {
  return state.segments;
}
